'use client';

import { useEffect, useRef } from 'react';

import { AccordionSpoilerProps } from './types';

type PreviousExpanded = {
  index: number;
  node: HTMLElement | null;
  height: number;
};

type ClickListenerProps = {
  index: number;
  maxScreen?: AccordionSpoilerProps['maxScreen'];
  disableScroll?: boolean;
  anchorConfig?: AccordionSpoilerProps['anchorConfig'];
  collapseOnChildrenClick?: boolean;
  collapseOnClick?: boolean;
};

const ClickListener = ({
  index,
  maxScreen,
  disableScroll,
  anchorConfig,
  // закрывать ли текущий открытый элемент при повторном нажатии
  collapseOnClick = true,
  collapseOnChildrenClick,
}: ClickListenerProps) => {
  const {
    // если true, то происходит отключение логики проставления якорей в url при клике на спойлеры и логики скролла к якорю
    enableDefaultScrolling = false,
    id: anchorId,
    // включение логики якорей только на экранах больше minScreen
    minScreen: anchorMinScreen,
  } = anchorConfig || {};

  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // отключение логики спойлера, если ширина экрана больше maxScreen
    const disableListener =
      maxScreen && screenWidths[maxScreen] <= window.innerWidth;

    if (!ref.current || disableListener) return;

    const currentElem = ref.current.parentElement;
    const childrenElem = currentElem?.querySelector('.children-selector');

    // аттрибут listenerLoaded нужен, чтобы код ниже не выполнялся дважды
    if (!currentElem || currentElem.dataset.listenerLoaded) return;

    const enableAnchors =
      enableDefaultScrolling &&
      (!anchorMinScreen || window.innerWidth >= screenWidths[anchorMinScreen]);

    const openCurrentSpoiler = (
      isExpanded: boolean,
      previousExpanded?: PreviousExpanded
    ) => {
      const siblings = Array.from(currentElem.parentElement?.children || []);

      // закрываем все элементы
      siblings.forEach((n, i) => {
        const sibling = n as HTMLElement;
        if (sibling.dataset.expanded === `true` && previousExpanded) {
          previousExpanded.height = sibling.getBoundingClientRect().height;
          previousExpanded.node = sibling;
          previousExpanded.index = i;
        }
        sibling.dataset['positionToExpanded'] = i > index ? 'after' : 'before';

        sibling.dataset.expanded = `false`;
      });

      if (!isExpanded) {
        currentElem.dataset['positionToExpanded'] = '';
        // открываем текущий элемент
        currentElem.dataset.expanded = `true`;
      }
    };

    const hash = window.location.hash;

    // если в url есть якорь, то открываем спойлер при открытии страницы (даже если enableAnchors === false)
    if (anchorId && hash === `#${anchorId}`) {
      openCurrentSpoiler(false);

      // если логика проставления якорей в url отключена (enableAnchors === false), то при клике на другие элементы списка якорь в url не будет меняться, из-за чего текущий элемент будет оставаться открытым, поэтому добавляем класс close-after-expanded, который отключит стилизацию элемента spoiler-content по якорю (div:target) и закроет текущий элемент при клике на другие элементы списка
      if (!enableAnchors) {
        const spoilerContentWrapper = currentElem.querySelector(
          '.spoiler-content-selector'
        );
        spoilerContentWrapper?.classList.add('close-after-expanded');
      }
    }

    currentElem.onclick = (e) => {
      const isLinkTag =
        e.target &&
        'tagName' in e.target &&
        typeof e.target.tagName === 'string' &&
        e.target.tagName.toLowerCase() === 'a';

      if (isLinkTag) {
        return;
      }

      const isClickOutsideChildren = !childrenElem?.contains(e.target as Node);
      if (collapseOnChildrenClick && isClickOutsideChildren) return;

      if (enableAnchors) e.preventDefault();

      const isExpanded = currentElem.dataset.expanded === `true`;
      const closeExpanded = isExpanded && collapseOnClick;
      if (isExpanded && !collapseOnClick) {
        return;
      }

      const previousExpanded: PreviousExpanded = {
        index: 0,
        node: null,
        height: 0,
      };

      openCurrentSpoiler(isExpanded, previousExpanded);

      // первый элемент в списке обрабатываем отдельно
      if (index === 0) {
        if (enableAnchors) {
          window.location.href = closeExpanded ? `#` : '#' + anchorId;
          return;
        }

        const parentRect = currentElem?.getBoundingClientRect();
        if (!disableScroll) {
          const y = parentRect.top + window.scrollY;

          window.scrollTo({ top: y - 100, behavior: 'smooth' });
        }

        return;
      }

      const previousSibling = currentElem.previousElementSibling;

      const targetToScrollNode = previousSibling;
      if (targetToScrollNode) {
        const targetRect = targetToScrollNode?.getBoundingClientRect();

        const y = targetRect.top + window.scrollY;

        if (enableAnchors) {
          window.location.href = closeExpanded ? `#` : '#' + anchorId;
        }

        // если открываемый спойлер идет сразу после текущего
        const isNextAfterPrevious = index === previousExpanded.index + 1;

        if (isNextAfterPrevious && !disableScroll) {
          return window.scrollTo({
            top: y,
            behavior: 'smooth',
          });
        }

        if (index === previousExpanded.index) return;

        const offset =
          index > previousExpanded.index ? previousExpanded.height : 200;

        if (!disableScroll) {
          window.scrollTo({
            top: y - offset,
            behavior: 'smooth',
          });
        }
      }
    };

    currentElem.dataset.listenerLoaded = 'true';
  }, []);

  return <div ref={ref} className='hidden' />;
};

export default ClickListener;

const screenWidths = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  always: 3840,
};
